// src/Leaderboard.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./leaderboard.css";
import { api } from "./api/client";
import constants from "./data/constants.json";

/**
 * Backend response:
 * {
 *   houses: [
 *     { id, name, points_total, top_students: [{ id, name, points }, ...] }
 *   ]
 * }
 *
 * Leaderboard shows:
 *  - Icon (mapped name → PNG in /public/icons/)
 *  - House name + total points
 *  - Top 3 students for the house, each with their points shown in the circle
 */

function fmt(n) {
  const v = Number(n ?? 0);
  return Number.isFinite(v) ? v.toLocaleString() : "0";
}

/** Fallback brand colors from your style guide */
const FALLBACK_BRAND_COLORS = {
  SAVANT: "#00aecc",
  JUNBI: "#509c49",
  TAFAKARI: "#f9a21a",
  UMUSA: "#702775",
  LAULIMA: "#7d9bc0",
};

// Read from constants.houses when available; otherwise use the fallback map
function colorForHouse(name) {
  const key = (name || "").toUpperCase();
  const fromConstants = constants?.houses?.[name] ?? constants?.houses?.[key];
  if (typeof fromConstants === "string") return fromConstants;
  if (fromConstants && typeof fromConstants === "object" && fromConstants.color) {
    return fromConstants.color;
  }
  return FALLBACK_BRAND_COLORS[key] || "#666";
}

/** Choose black/white based on background for legibility */
function contrastTextOn(hex) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return "#000";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000; // W3C-ish contrast heuristic
  return yiq >= 140 ? "#000" : "#fff";
}

/**
 * Map backend house names to actual filenames in /public/icons
 * IMPORTANT: case-sensitive for Vercel
 */
const ICON_MAP = {
  JUNBI: "Junbi.png",
  LAULIMA: "Laulima.png",
  SAVANT: "Savant.png",
  TAFAKARI: "Tafakari.png",
  UMUSA: "Umusa.png",
};

function iconUrl(name) {
  const key = (name || "").toUpperCase();
  const file = ICON_MAP[key];
  return file ? `/icons/${file}` : "";
}

async function safeGet(path) {
  try {
    return await api.get(path);
  } catch (e) {
    return { ok: false, status: e?.status || 0, data: e?.data || {} };
  }
}

const SCHOOL_LEVEL_OPTIONS = [
  { value: "", label: "All Grades" },
  { value: "ELEMENTARY", label: "Elementary (K-5)" },
  { value: "MIDDLE", label: "Middle (6-8)" },
  { value: "HIGH", label: "High (9-12)" },
];

export default function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [schoolLevel, setSchoolLevel] = useState("");
  const inFlightRef = useRef(false);

  const fetchLeaderboard = useCallback(async (level) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setBusy(true);
    setError("");

    const query = level ? `?school_level=${encodeURIComponent(level)}` : "";
    let res = await safeGet(`/api/leaderboard/${query}`);
    if (!res.ok) res = await safeGet(`/api/leaderboard${query}`);

    if (!res.ok) {
      const msg =
        res?.data?.detail ||
        res?.data?.message ||
        `Failed to load leaderboard (status ${res.status || "ERR"}).`;
      setError(msg);
      setBusy(false);
      inFlightRef.current = false;
      return;
    }

    const houses = Array.isArray(res?.data?.houses) ? res.data.houses : [];
    const mapped = houses.map((h) => ({
      id: h.id,
      name: h.name,
      color: colorForHouse(h.name),
      icon: iconUrl(h.name),
      points: Number(h.points_total || 0),
      topStudents: Array.isArray(h.top_students)
        ? h.top_students.slice(0, 3).map((s) => ({
            id: s.id,
            name: s.name,
            points: Number(s.points || 0),
          }))
        : [],
    }));

    mapped.sort((a, b) => (b.points ?? 0) - (a.points ?? 0));
    setRows(mapped.slice(0, 5));

    setBusy(false);
    setLastUpdated(new Date());
    inFlightRef.current = false;
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await fetchLeaderboard(schoolLevel);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || "Failed to load leaderboard.");
        setBusy(false);
        inFlightRef.current = false;
      }
    })();
    return () => {
      alive = false;
    };
  }, [fetchLeaderboard, schoolLevel]);

  const derived = useMemo(
    () =>
      rows.map((h, idx) => ({
        ...h,
        rank: idx + 1,
        text: contrastTextOn(h.color),
      })),
    [rows]
  );

  const rankLabel = (n) => (["1st", "2nd", "3rd", "4th", "5th"][n - 1] || `${n}th`);

  return (
    <div className="leaderboard-page">
      <div className="leaderboard__header">
        <h2>School Leaderboard</h2>
        <div className="leaderboard__controls">
          <select
            className="leaderboard-filter"
            value={schoolLevel}
            onChange={(e) => setSchoolLevel(e.target.value)}
            disabled={busy}
            aria-label="Filter leaderboard by school level"
          >
            {SCHOOL_LEVEL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            className="leaderboard-refresh"
            onClick={() => fetchLeaderboard(schoolLevel)}
            disabled={busy}
            aria-busy={busy ? "true" : "false"}
          >
            Refresh
          </button>
        </div>
      </div>

      {lastUpdated ? (
        <div className="leaderboard__timestamp">
          Updated at {lastUpdated.toLocaleTimeString()}
        </div>
      ) : null}
      {busy ? <div className="leaderboard-loading">Loading leaderboard…</div> : null}
      {error ? <div className="leaderboard-error">{error}</div> : null}

      {!busy && !error && derived.length > 0 ? (
        <div className="tiles-grid">
          {derived.map((h) => (
            <Tile key={h.name} h={h} rankLabel={rankLabel} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Tile({ h, rankLabel }) {
  const [showInitials, setShowInitials] = useState(false);

  return (
    <div
      className="tile"
      style={{
        backgroundColor: h.color,
        color: h.text, // everything inside inherits this for readability
        borderColor: "transparent",
      }}
    >
      {/* rank chip over the brand background */}
      <div
        className="tile-rank"
        style={{
          color: h.text,
          background: "rgba(255,255,255,0.18)",
          borderColor: "rgba(255,255,255,0.35)",
        }}
      >
        {rankLabel(h.rank)}
      </div>

      <div
        className="tile-icon-wrap"
        style={{
          borderColor: h.text,
          background: "rgba(255,255,255,0.15)",
        }}
      >
        {!showInitials && h.icon ? (
          <img
            src={h.icon}
            alt={`${h.name} icon`}
            className="tile-icon"
            onError={() => setShowInitials(true)}
            title={h.icon}
          />
        ) : (
          <div
            className="tile-student-badge"
            style={{ borderColor: h.text, background: "rgba(255,255,255,0.15)" }}
          >
            <span className="tile-student-initials" style={{ color: h.text }}>
              {h.name
                .split(/\s+/)
                .filter(Boolean)
                .map((s) => s[0]?.toUpperCase())
                .slice(0, 2)
                .join("") || "—"}
            </span>
          </div>
        )}
      </div>

      <div className="tile-name">{h.name}</div>
      <div className="tile-points" style={{ color: h.text }}>
        {fmt(h.points)} Pts
      </div>

      <div className="tile-divider" style={{ background: "rgba(255,255,255,0.35)" }} />

      <div className="tile-students-list">
        {h.topStudents && h.topStudents.length > 0 ? (
          h.topStudents.map((s, idx) => (
            <div className="tile-student-row" key={s.id ?? `${s.name}-${idx}`}>
              <div
                className="tile-student-badge"
                style={{ borderColor: h.text, background: "rgba(255,255,255,0.15)" }}
              >
                <span className="tile-student-points" style={{ color: h.text }}>
                  {fmt(s.points)}
                </span>
              </div>
              <div className="tile-student-text">
                <div className="tile-student-name">{s.name}</div>
              </div>
            </div>
          ))
        ) : (
          <div className="tile-student-row">
            <div
              className="tile-student-badge"
              style={{ borderColor: h.text, background: "rgba(255,255,255,0.15)" }}
            >
              <span className="tile-student-points" style={{ color: h.text }}>
                —
              </span>
            </div>
            <div className="tile-student-text">
              <div className="tile-student-name">No students yet</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
